import React, { forwardRef, useState, useEffect, PropsWithChildren } from 'react';
import {
  FloatingOverlay,
  FloatingPortal,
  FloatingFocusManager,
  useInteractions,
  useDismiss,
  useFloating,
  useRole,
  useMergeRefs,
  useFloatingTree,
  useFloatingNodeId,
  OpenChangeReason,
  FloatingNode,
  ReferenceType,
  FloatingTreeType,
} from '@floating-ui/react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { CSSValueWithLength, buildCSSWithLength, getZIndex } from '../../styles';
import { FLOATING_ROOT_ID, FLOATING_ORDER } from '../../constants';
import { semantic_colors, shapes } from '../../foundation';
import { FloatingTreeContext } from '../context';
import { useManualDismiss } from '../modal/useManualDismiss';

export type DrawerDirection = 'left' | 'right';

export interface DrawerProps {
  className?: string;
  /** @default 'right' */
  direction?: DrawerDirection;
  /** @default getZIndex('drawer') */
  zIndex?: number;
  /** @default 280 */
  width?: CSSValueWithLength;
  opened?: boolean;
  /**
   * outside 영역 클릭시 닫힘 사용 여부
   * @default true
   */
  canClickOutside?: boolean;
  /**
   * escapeKey 키이벤트 닫힘 사용 여부
   * @default true
   */
  canCloseEscapeKey?: boolean;
  /**
   * main app node 바깥영역으로 생성할지 여부
   * @default false
   */
  noUsePortal?: boolean;
  /**
   * 오버레이가 렌더링되는 동안 스크롤 금지 여부
   * @default true
   */
  lockScroll?: boolean;
  /**
   * Drawer 닫힐 때 애니메이션 사용 여부
   * @default true
   */
  animationOnClose?: boolean;
  onClose?: () => void;
}

const MotionFloatingOverlay = motion(FloatingOverlay);

const DrawerInner = forwardRef<HTMLDivElement, PropsWithChildren<DrawerProps>>(
  (
    {
      className,
      children,
      direction = 'right',
      zIndex = getZIndex('drawer'),
      width = 280,
      opened: openProp,
      canClickOutside = true,
      canCloseEscapeKey = true,
      noUsePortal = false,
      lockScroll = true,
      animationOnClose = true,
      onClose,
    },
    ref,
  ) => {
    const nodeId = useFloatingNodeId();
    const tree = useFloatingTree();
    const floatingOverlayId = `${FLOATING_ROOT_ID}-overlay-${nodeId}`;

    const [opened, setOpened] = useState(openProp);
    const directionAmount = direction === 'left' ? '-100%' : '100%';
    const containerPosition = noUsePortal ? 'absolute' : 'fixed';
    const containerMotionVariants = {
      hidden: { opacity: 0, x: directionAmount },
      visible: {
        opacity: 1,
        x: 0,
        transition: { type: 'easeIn', duration: 0.2 },
      },
      exit: {
        opacity: 0,
        x: directionAmount,
        transition: { type: 'easeIn', duration: 0.25 },
      },
    };
    const { context } = useFloating({
      open: opened,
      nodeId,
    });
    const { getFloatingProps } = useInteractions([
      useRole(context, { role: 'dialog' }),
      useDismiss(context, {
        escapeKey: canCloseEscapeKey,
        outsidePress: canClickOutside,
        bubbles: false,
      }),
    ]);
    const floatingRef = useMergeRefs([context.refs.setFloating, ref]);

    useEffect(() => {
      if (openProp !== undefined) {
        setOpened(openProp);
      }
    }, [openProp]);

    useEffect(() => {
      !opened && onClose?.();
    }, [opened]);

    const handleManualDismiss = (reason: OpenChangeReason) => {
      const convertCancelEvent = {
        ['outside-press']: 'overlayClick',
        ['escape-key']: 'escapeKeyPress',
        'ancestor-scroll': undefined,
        'click': undefined,
        'focus': undefined,
        'hover': undefined,
        'list-navigation': undefined,
        'reference-press': undefined,
        'safe-polygon': undefined,
      }[reason];
      if (convertCancelEvent) {
        onClose?.();
        setOpened(false);
      }
    };
    useManualDismiss({
      controlled: true,
      rootId: noUsePortal ? floatingOverlayId : FLOATING_ROOT_ID,
      tree: tree as FloatingTreeType<ReferenceType>,
      context,
      callbackTypeWithFn: handleManualDismiss,
    });

    const renderToDrawer = () => {
      return (
        <MotionFloatingOverlay
          id={floatingOverlayId}
          lockScroll={lockScroll}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={animationOnClose ? { opacity: 0 } : undefined}
          transition={{ duration: 0.2 }}
          style={{ position: containerPosition, background: semantic_colors.background.scrim, zIndex }}
        >
          <ContentContainer
            width={width}
            direction={direction}
            variants={containerMotionVariants}
            initial='hidden'
            animate='visible'
            exit={animationOnClose ? 'exit' : undefined}
            {...getFloatingProps({
              className: classNames('pds-drawer', className),
              style: {
                position: containerPosition,
              },
              ref: floatingRef,
            })}
          >
            {children}
          </ContentContainer>
        </MotionFloatingOverlay>
      );
    };

    return noUsePortal ? (
      <FloatingNode id={nodeId}>
        <AnimatePresence>
          {opened && (
            <FloatingFocusManager context={context} order={FLOATING_ORDER}>
              {renderToDrawer()}
            </FloatingFocusManager>
          )}
        </AnimatePresence>
      </FloatingNode>
    ) : (
      <FloatingNode id={nodeId}>
        <AnimatePresence>
          {opened && (
            <FloatingPortal id={FLOATING_ROOT_ID}>
              <FloatingFocusManager context={context} order={FLOATING_ORDER}>
                {renderToDrawer()}
              </FloatingFocusManager>
            </FloatingPortal>
          )}
        </AnimatePresence>
      </FloatingNode>
    );
  },
);

const DrawerWithContext = FloatingTreeContext(DrawerInner);
export const Drawer = forwardRef<HTMLDivElement, PropsWithChildren<DrawerProps>>((props, ref) => (
  <DrawerWithContext {...props} ref={ref} />
));

const ContentContainer = styled(motion.div)<{ width: CSSValueWithLength; direction: DrawerDirection }>`
  top: 0;
  bottom: 0;
  height: 100%;
  background-color: ${semantic_colors.background.surface};
  ${({ width }) => buildCSSWithLength('width', width)};
  ${({ direction }) => buildCSSWithLength(direction, 0)}
  ${shapes.shadow.high}
  &:focus {
    outline: none;
  }
`;
